import React from 'react';
import PropTypes from 'prop-types';
import columns from '../constants/columns';
import deepCopy from '../../../utilities/deepCopy';
import isCartPastLimit from '../../../helpers/cart/isCartPastLimit';

import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

const CartTable = ({
	data,
	generateSubtotal,
	removeItemFromCart,
	siteId,
	subtotal,
	updateItems,
	userBalance
}) => {
	const [cartInventory, setCartInventory] = React.useState(data);

	React.useEffect(() => {
		if (JSON.stringify(cartInventory) !== JSON.stringify(data)) {
			setCartInventory(data);
		}
	}, [data]);

	const determineSubtotal = (price, quantity) => {
		let subtotal = price;

		subtotal = quantity && price * quantity;

		return subtotal;
	};

	const updateItemQuantity = (event, giftCard) => {
		const copiedInventory = deepCopy(cartInventory);

		const updatedQuantityIndex = copiedInventory.findIndex((item) => item?.id === giftCard.id);

		if (updatedQuantityIndex !== -1 && event.target.value > 0) {
			copiedInventory[updatedQuantityIndex].quantity = parseInt(event.target.value);
			setCartInventory(copiedInventory);
			updateItems(copiedInventory);
		}
	};

	const deleteFromCart = (id) => {
		removeItemFromCart(id);
		generateSubtotal();
	};

	return (
		<TableContainer
			component={Paper}
			sx={{
				width: { xs: '92%', sm: '96%', lg: '85%', xl: '65%' },
				overflow: 'auto'
			}}
		>
			<Table className='filter' aria-label='cart-table'>
				<TableHead>
					<TableRow sx={{ bgcolor: 'lightgrey' }}>
						{columns.map((column) => {
							return (
								<TableCell
									colSpan={column.colSpan}
									width={column.width}
									align={column.align}
									key={column.title}
								>
									{column.title}
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody className='filter'>
					{cartInventory.map((giftCard) => {
						return (
							<TableRow key={giftCard.id}>
								<TableCell>
									<Tooltip title={<Typography variant='body2'>Remove item</Typography>}>
										<IconButton
											onClick={() => {
												window.scrollTo({ top: 0 });
												deleteFromCart(giftCard.id);
											}}
										>
											<CancelOutlinedIcon />
											<Typography variant='div' sx={{ display: 'none' }}>
												Remove Gift Card
											</Typography>
										</IconButton>
									</Tooltip>
								</TableCell>
								<TableCell>
									<Box
										component='img'
										src={giftCard.image}
										sx={{ width: { xs: '40px', md: '70px' } }}
										alt={`$${giftCard.amount} ${giftCard.company} Gift Card`}
									/>
								</TableCell>
								<TableCell colSpan={3}>
									${giftCard.amount} {giftCard.company} Gift Card
								</TableCell>
								<TableCell>${giftCard.price}.00</TableCell>
								<TableCell align='center'>
									<FormLabel
										htmlFor='cartgcQty'
										id='cartgcQty'
										style={{ visibility: 'hidden', position: 'absolute', overflow: 'hidden' }}
									>
										Quantity
									</FormLabel>
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<Button
											variant={giftCard?.quantity > 1 ? 'outlined' : ''}
											aria-label={`Decrease quantity ${giftCard.company} Gift Card, Current Quantity ${giftCard?.quantity}`}
											sx={{
												minWidth: '30px',
												padding: 0,
												borderColor: '#fff',
												color: '#666',
												fontSize: '1.6em'
											}}
											onClick={() =>
												updateItemQuantity(
													{ target: { value: (giftCard?.quantity || 0) - 1 } },
													giftCard
												)
											}
											disabled={giftCard?.quantity === 1}
										>
											-
										</Button>

										<Typography
											sx={{ width: '20px', mx: 1 }}
											aria-label={`Quantity of $${giftCard.amount} ${giftCard.company} Gift Card`}
										>
											{giftCard?.quantity || ''}
										</Typography>

										<Button
											variant={
												isCartPastLimit(subtotal, siteId, userBalance?.totalLeft) ? '' : 'outlined'
											}
											aria-label={`Increase quantity ${giftCard.company} Gift Card, Current Quantity ${giftCard?.quantity}`}
											sx={{
												minWidth: '30px',
												padding: 0,
												borderColor: '#fff',
												color: '#666',
												fontSize: '1.6em'
											}}
											onClick={() =>
												updateItemQuantity(
													{ target: { value: (giftCard?.quantity || 0) + 1 } },
													giftCard
												)
											}
											disabled={isCartPastLimit(subtotal, siteId, userBalance?.totalLeft)}
										>
											+
										</Button>
									</Box>
								</TableCell>
								<TableCell align='right'>
									${determineSubtotal(giftCard?.price, giftCard?.quantity)}.00
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

CartTable.propTypes = {
	data: PropTypes.array,
	generateSubtotal: PropTypes.func,
	removeItemFromCart: PropTypes.func,
	siteId: PropTypes.number,
	subtotal: PropTypes.number,
	updateItems: PropTypes.func,
	userBalance: PropTypes.object
};

export default CartTable;
